import type { LucideIcon, LucideProps } from 'lucide-react';
import type { JSX } from 'react';
import {
  Archive,
  ArchiveRestore,
  ArrowDown,
  ArrowUp,
  Bell,
  BookOpen,
  Bot,
  Brain,
  Building,
  Calendar,
  ChartColumn,
  Check,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ChevronUp,
  Circle,
  CircleAlert,
  CircleCheck,
  CircleMinus,
  CircleQuestionMark,
  CircleX,
  Clock,
  CodeXml,
  Copy,
  Cpu,
  CreditCard,
  Database,
  Download,
  Ellipsis,
  EllipsisVertical,
  ExternalLink,
  Eye,
  File,
  FileClock,
  FileCode,
  FilePlus,
  FileSpreadsheet,
  FileText,
  Folder,
  FolderOpen,
  FolderPlus,
  Funnel,
  Gauge,
  Gift,
  GitBranch,
  House,
  Globe,
  Image,
  Info,
  KeyRound,
  Languages,
  Layers,
  LayoutGrid,
  Lightbulb,
  Link,
  Link2,
  ListChecks,
  Lock,
  LogIn,
  LogOut,
  MessageCircle,
  Mail,
  MapPin,
  Menu,
  MessageSquare,
  MessagesSquare,
  Monitor,
  Moon,
  PanelLeft,
  PanelRight,
  Paperclip,
  Pencil,
  Pin,
  PinOff,
  Play,
  Plug,
  Plus,
  Presentation,
  RefreshCw,
  RotateCcw,
  Scale,
  Search,
  Send,
  Server,
  Settings,
  Shield,
  ShieldCheck,
  SlidersHorizontal,
  Sparkles,
  Split,
  Square,
  SquareTerminal,
  Sun,
  Telescope,
  ThumbsDown,
  ThumbsUp,
  Trash,
  TrendingUp,
  TriangleAlert,
  Undo2,
  Upload,
  User,
  UserPlus,
  Users,
  Video,
  Wrench,
  X,
  Zap,
} from 'lucide-react';

/**
 * The icon set (thin strokes, docs/design/README.md). Pages use these names,
 * never a library directly, so the set stays one consistent family. No
 * third-party brand logos.
 */
export type IconProps = Omit<LucideProps, 'ref'>;
export type IconComponent = (props: IconProps) => JSX.Element;

function icon(Base: LucideIcon): IconComponent {
  return function Icon({ size = 18, strokeWidth = 1.75, className, ...rest }: IconProps) {
    return <Base size={size} strokeWidth={strokeWidth} aria-hidden="true" focusable="false" className={className ? `ui-icon ${className}` : 'ui-icon'} {...rest} />;
  };
}

export const Icons = {
  // navigation and places
  home: icon(House),
  skills: icon(Sparkles),
  connections: icon(Link2),
  documents: icon(FileText),
  workspace: icon(Folder),
  workspaceOpen: icon(FolderOpen),
  history: icon(MessageSquare),
  conversations: icon(MessagesSquare),
  /** one conversation (CalismaAlanlari.dc.html rows) */
  chat: icon(MessageCircle),
  grid: icon(LayoutGrid),
  access: icon(Shield),
  models: icon(Cpu),
  integrations: icon(Link),
  usage: icon(TrendingUp),
  policies: icon(ShieldCheck),
  audit: icon(FileClock),
  platform: icon(Server),
  organization: icon(Building),
  settings: icon(Settings),
  memory: icon(Brain),
  members: icon(Users),
  user: icon(User),
  invite: icon(UserPlus),
  bell: icon(Bell),
  code: icon(CodeXml),
  menu: icon(Menu),
  sidebar: icon(PanelLeft),
  panelRight: icon(PanelRight),
  login: icon(LogIn),
  logout: icon(LogOut),
  // actions
  plus: icon(Plus),
  more: icon(Ellipsis),
  moreVertical: icon(EllipsisVertical),
  close: icon(X),
  search: icon(Search),
  filter: icon(Funnel),
  send: icon(ArrowUp),
  arrowDown: icon(ArrowDown),
  sendMail: icon(Send),
  stop: icon(Square),
  attach: icon(Paperclip),
  copy: icon(Copy),
  regenerate: icon(RefreshCw),
  thumbsUp: icon(ThumbsUp),
  thumbsDown: icon(ThumbsDown),
  undo: icon(Undo2),
  restore: icon(RotateCcw),
  edit: icon(Pencil),
  branch: icon(GitBranch),
  fork: icon(Split),
  download: icon(Download),
  upload: icon(Upload),
  preview: icon(Eye),
  external: icon(ExternalLink),
  trash: icon(Trash),
  archive: icon(Archive),
  unarchive: icon(ArchiveRestore),
  pin: icon(Pin),
  unpin: icon(PinOff),
  play: icon(Play),
  sliders: icon(SlidersHorizontal),
  // chevrons
  chevronDown: icon(ChevronDown),
  chevronUp: icon(ChevronUp),
  chevronRight: icon(ChevronRight),
  chevronLeft: icon(ChevronLeft),
  // states
  check: icon(Check),
  done: icon(CircleCheck),
  failed: icon(CircleX),
  skipped: icon(CircleMinus),
  pending: icon(Circle),
  error: icon(CircleAlert),
  info: icon(Info),
  warning: icon(TriangleAlert),
  question: icon(CircleQuestionMark),
  time: icon(Clock),
  // things
  file: icon(File),
  fileCode: icon(FileCode),
  fileNew: icon(FilePlus),
  folderNew: icon(FolderPlus),
  spreadsheet: icon(FileSpreadsheet),
  slides: icon(Presentation),
  image: icon(Image),
  web: icon(Globe),
  terminal: icon(SquareTerminal),
  monitor: icon(Monitor),
  plan: icon(ListChecks),
  tool: icon(Wrench),
  bot: icon(Bot),
  plug: icon(Plug),
  key: icon(KeyRound),
  // the free trial (LisansDeneme, LisansDenemeRozet)
  gift: icon(Gift),
  lock: icon(Lock),
  shield: icon(Shield),
  mail: icon(Mail),
  // Phase 16c: payments
  card: icon(CreditCard),
  gauge: icon(Gauge),
  chart: icon(ChartColumn),
  layers: icon(Layers),
  database: icon(Database),
  calendar: icon(Calendar),
  // a meeting's place in the calendar card (MsTakvimYatay): online, or a room
  video: icon(Video),
  location: icon(MapPin),
  book: icon(BookOpen),
  idea: icon(Lightbulb),
  zap: icon(Zap),
  scale: icon(Scale),
  research: icon(Telescope),
  language: icon(Languages),
  sun: icon(Sun),
  moon: icon(Moon),
} satisfies Record<string, IconComponent>;

export type IconName = keyof typeof Icons;
